import * as z from 'zod';
import { localeSchema, strict } from './common.js';
import { passIssuanceModeSchema } from './credential.js';
import { noBandAtRegistrationSchema } from './settings.js';
/**
 * The four platform routes' bodies: the two probes, the client bootstrap and the metrics scrape.
 *
 * **They are contracts even though nothing in the browser validates them**, because they are the
 * first thing an integrator meets and the only part of the API an orchestrator ever reads. Written
 * here rather than inferred from the handlers, they are what the OpenAPI document and the drift
 * gate compare the implementation against — and a probe whose shape changed without anybody
 * noticing is a readiness check that silently stops meaning what a deployment thinks it means.
 *
 * **`/api/v1/ready` answers the SAME body at 200 and at 503**, deliberately, and that is the one
 * thing about this file worth reading twice. It is the only route in the system whose failure is
 * not a refusal: nobody asked for anything, so there is no AD-35 error contract to render. An
 * orchestrator reads the status; an operator reads the checks.
 *
 * **Neither probe carries a reason, a host or a driver message.** What an unauthenticated caller
 * learns from `ready` is the set of dependency NAMES and a boolean each, which is what the README
 * already says; "why" is where a connection string and a role name live.
 */
/** `GET /api/v1/health`: liveness, which answers while every dependency is down. */
export const healthSchema = strict({
    status: z.literal('ok'),
    version: z.string().min(1).max(64),
    uptimeSeconds: z.number().nonnegative(),
});
/** `GET /api/v1/ready`: one boolean per dependency, and the conjunction of them. */
export const readySchema = strict({
    ready: z.boolean(),
    /** Keyed by the dependency's stable name. A name appears here and in no log line. */
    checks: z.record(z.string(), z.boolean()),
});
/**
 * `GET /api/v1/config`: what the browser needs before there is a session.
 *
 * **The test every member has to pass: it discloses nothing an operator could not read off the
 * building**, because this body is served to anybody who can reach the origin. Three of the five
 * are facts about the installation; the other two are a scanner timing in milliseconds and what
 * happens when a registration carries no band, and both are of a piece with the issuance mode,
 * which already tells a caller which of two registration procedures the facility runs.
 *
 * **It is deliberately NOT a general settings feed.** The rule this body is guarded by is that
 * nothing non-public may join it, because the failure mode of a bootstrap endpoint is that it
 * accretes. A member is added only when the browser cannot function correctly without it, which is
 * why the product caps are still imported from `limits.ts` instead.
 *
 * **`selfServiceReset` is an amendment of record to the half of that rule which said "no
 * environment value".** It is not one: it is a CAPABILITY, computed from whether this installation
 * has a mail server AND whether an administrator has left the feature on, and neither input is
 * disclosed. The login screen has no session, so `GET /api/v1/settings` is unreachable to it, and a
 * screen that offered a way back into an account this installation cannot actually mail would be
 * the "validates and does nothing" shape this project treats as a defect. What it discloses is
 * one bit that the same anonymous caller learns anyway by posting to the reset route and reading
 * whether it refuses. The guard the rule exists for is untouched, because it was never the word
 * "environment" that did the work — it is `CLIENT_CONFIG_KEYS` and the test over it, which is what
 * stops a seventh member arriving unnoticed.
 */
export const clientConfigSchema = strict({
    passIssuanceMode: passIssuanceModeSchema,
    defaultLocale: localeSchema,
    reportingTimezone: z.string().min(1).max(64),
    /**
     * AD-30's client-side double-read window, from `scan.client_burst_dedupe_ms`.
     *
     * It is here because there is nowhere else it could arrive: an operator cannot read
     * `GET /api/v1/settings`, which is admin-only, and the gate screen is where the value is used. A
     * browser that fell back to the shipped default would make the setting one an administrator can
     * store, have audited, and never have honoured.
     *
     * **Two limits worth knowing before it is relied on.** The browser fetches this body once, before
     * its first render, so a change reaches a terminal on that terminal's next page load rather than
     * mid-shift; the server serving it per request fixes stale-until-restart, not stale-until-reload.
     * And this schema is `strict()` and the browser validates what it receives, so ADDING a member is
     * a breaking change for an already-loaded tab: one that reloads the API but not the script refuses
     * the body and renders the configuration error screen. One hard reload, once.
     */
    scanDuplicateWindowMs: z.int().min(0).max(5000),
    /**
     * What `passes.no_band_at_registration` says, which decides what a screen may OFFER when the
     * operator has no band in their hand.
     *
     * It arrives here for the reason `scanDuplicateWindowMs` does and passes the same test. An
     * operator cannot read `GET /api/v1/settings`, and the registration screen needs the answer
     * before anything is drawn — a band is required in `preprinted` mode, so without this the
     * escape an administrator switched on for the day the box is empty would change nothing any
     * screen could reach, and nobody could be registered at all until a new batch was imported.
     *
     * **What it discloses, stated rather than assumed:** which of three registration procedures this
     * installation runs when a band is absent. That is of a piece with `passIssuanceMode`, which
     * already tells the same caller which of two procedures it runs at all; it is not a credential,
     * and acting on it still requires a session holding `credential:issue`. Putting it on the
     * authenticated session payload instead would disclose nothing, and was rejected because the
     * registration screen reads its configuration before a session exists.
     *
     * **The server stays the authority.** A screen that offers a control is not what permits the act:
     * `credentials/service.ts` re-reads the setting inside the transaction, so a stale value in a tab
     * open since this morning is refused with `CREDENTIAL_CODE_REQUIRED` rather than honoured.
     *
     * **The browser never compares this value.** `app/providers/config.tsx` turns it into named
     * booleans and every screen asks those, for the reason that module's own note gives about the
     * issuance mode: a literal spread across components is a branch that compiles in both directions
     * and is quietly wrong for months in one of them.
     */
    noBandAtRegistration: noBandAtRegistrationSchema,
    /**
     * Whether Section 1.7's self-service reset is offered at all.
     *
     * True exactly when this installation has a mail server configured AND
     * `security.self_service_reset` is on. The browser renders the two links under the sign-in form
     * from this and nothing else; the routes themselves refuse with `SYSTEM_FEATURE_DISABLED` on the
     * same predicate, so a hidden control and a closed door are one decision rather than two.
     *
     * A boolean rather than the inputs, so neither `SMTP_HOST`'s presence nor a settings value is
     * disclosed as such, and so the browser has nothing to compare and get backwards.
     */
    selfServiceReset: z.boolean(),
    /**
     * Whether this installation controls meals, from `dining.enabled`.
     *
     * It passes the test every member of this body has to pass: what it discloses is one bit, that
     * this facility runs a counter, which is of a piece with `passIssuanceMode` already telling the
     * same anonymous caller which of two registration procedures it runs. It is not a capability
     * anybody gains by learning it; every dining route is authorized twice regardless.
     *
     * It is here rather than on the authenticated session payload because the administration
     * dashboard has to decide whether to offer the dining panel, and an administrator cannot read
     * `GET /api/v1/settings`, which is the lead administrator's. The COUNTER does not read this at
     * all: the scan's own `dining` block is `null` when the feature is off, which is a structural
     * signal rather than a flag a screen has to remember to consult.
     *
     * **Unlike the resolve response, adding a member here breaks no open tab.** This body is fetched
     * once before the first render and held in a React context, so a session that started before the
     * upgrade never re-fetches it.
     */
    diningEnabled: z.boolean(),
});
/**
 * `PUT /api/v1/maintenance`: what the write answers with.
 *
 * The flag and nothing else. The full status — including the reason and who armed it — is the GET
 * beside it, so the write does not become a second, thinner answer to the same question.
 */
export const maintenanceSetSchema = strict({ active: z.boolean() });
//# sourceMappingURL=system.js.map